import { Injectable } from '@angular/core';
import { ArticleManager } from '@article/manager/article.manager';
import { IsProjectType } from '@project/model/is-project-type';
import { WireContourService } from '@3d-scene/common/util/wire-contour-service';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { BackgroundTasksInfoService } from '@project/service/background-tasks-info.service';
import { AlertButton } from '@shared/alert/alertButton';
import { Log } from '@shared/debug/log';
import { lastValueFrom } from 'rxjs';
import { GlobalArticleContainerUtils } from '@3d-scene/model3d/util/global-article-container-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectOrderingUtils } from '../../components/project/util/project-ordering-utils';
import { ProjectUtils } from '../../components/project/util/project-utils';
import { ProjectModel } from '../../model/project.model';
import { RepresentationService } from '../../service/representation.service';
import { DataSheetDataService } from './data-sheet-data.service';

@Injectable({
  providedIn: 'root'
})
export class DataSheetService {

  constructor(
    private representationService: RepresentationService,
    private projectModel: ProjectModel,
    private dataSheetDataService: DataSheetDataService,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper,
    private globalArticleContainerUtils: GlobalArticleContainerUtils,
    private backgroundTasksInfoService: BackgroundTasksInfoService,
    private wireContourService: WireContourService,
    private containerService: ContainerService,
    private articleManager: ArticleManager,
  ) {
  }

  public canOpenCustomDataSheet(): boolean {
    return IsProjectType.mcsGds(this.projectModel.currentProject) || IsProjectType.splicingConnector221(this.projectModel.currentProject);
  }

  public async openCustomDataSheet(): Promise<void> {
    const articleId = ProjectUtils.getBaseArticleId(this.projectModel.currentProject);

    if (articleId) {
      const article = await lastValueFrom(this.articleManager.getArticleById(articleId));

      if(!article?.publicGlobal) {
        this.alertHelper.showError("dataSheet.error.title", "dataSheet.error.message.article_not_public");
        return;
      }
      try {
        this.backgroundTasksInfoService.setTaskActive("datasheet-export", true);
        await this.createDataSheet(articleId);
      } finally {
        this.backgroundTasksInfoService.setTaskActive("datasheet-export", false);
      }
    } else {
      Log.error("Base article id not found for project");
      await this.alertHelper.showError("global.title.error", "global.message.unexpected_error");
    }
  }

  private async createDataSheet(articleId: number): Promise<void> {
    const newTab = window.open();
    newTab?.document.write(this.temporaryNewTabContent());
    for (const container of  this.containerService.enumerateArticleContainersInGlobal()) {
      this.wireContourService.createWireContourForPdf(container);
    }
    // Datasheet screenshots require the representation data in ProjectVO.
    await lastValueFrom(this.representationService.scheduleUpdateForReportGeneration());
    const projectLink = ProjectOrderingUtils.prepareDirectLink(this.projectModel.currentProject);
    const boundingBox = this.globalArticleContainerUtils.calculateBBoxForAllArticlesInGlobal();

    const dataSheetResultPromise = lastValueFrom(this.dataSheetDataService.request(
      this.projectModel.currentProject,
      {min: boundingBox.min.toArray().map(v => v / 10), max: boundingBox.max.toArray().map(v => v / 10)},
      projectLink,
      this.localeResourceManager.currentLanguage.langKey,
      articleId
      )
    );

    for (const container of  this.containerService.enumerateArticleContainersInGlobal()) {
      this.wireContourService.createWireContour(container);
    }

    try {
      const dataSheetResult = await dataSheetResultPromise;

      if (!newTab || newTab?.closed) {
        this.handleNewTabClosed(dataSheetResult.link)
      } else {
        newTab.location.href = dataSheetResult.link;
      }
    } catch(ex) {
      newTab?.close();
      // @ts-ignore
      if (ex.status === 400) {
        this.alertHelper.showError("dataSheet.error.title", "dataSheet.error.message.service_currently_not_available");
      } else {
        this.alertHelper.showError("dataSheet.error.title", "dataSheet.error.message.error_generating_data_sheet");
      }
    }
  }

  private temporaryNewTabContent() {
    return `
    <h2 style="padding: 40px; font-family: system-ui">
        ${this.localeResourceManager.getValueByKey("dataSheet.generating.request")}
    </h2>
    <style>
      h2:after {
        overflow: hidden;
        display: inline-block;
        vertical-align: bottom;
        -webkit-animation: ellipsis 900ms infinite;
        animation: ellipsis 900ms infinite;
        content: "...";
        width: 0px;
      }

      @keyframes ellipsis {
        to {
          width: 50px;
        }
      }
    </style>
    `
  }

  private handleNewTabClosed(link: string) {
    this.alertHelper.showAlert("dataSheet.link", "dataSheet.newTabClosed", {0: link}, [AlertButton.OK]);
  }
}
